// Team reports — per-member performance roll-ups (the team counterpart to
// ReportsClientsView). Two tabs:
//   • "Pre-armados" — the built-in reports (production, hours, leads, tasks,
//     policies, activity, conversion, goals).
//   • "Mis reportes" — saved custom reports: pick a metric, columns, members,
//     a date preset and a chart, name it, share it, run it with one click.
//
// Los Paneles vivían aquí como una tercera pestaña y ahora son su propia
// pantalla (ReportDashboardsView, "Paneles de Reportes" en el menú): nadie los
// encontraba escondidos dentro de Reportes de Equipo. El catálogo de métricas
// que las dos pantallas necesitan salió a teamMetricsCatalog.jsx.
//
// Every row is one team member (agents AND seats) except `policies`, which
// groups by who processed, and `goals`, whose rows are goals. Counts come from
// /reports/team — aggregates only, no PHI. Saved reports persist to
// /reports/team-custom and dashboards to /reports/dashboards; both store only
// the recipe, so running one re-queries and can never show a stale number.

import { useEffect, useState } from 'react';
import DateField from './DateField.jsx';
import {
  Download, ArrowLeft, BarChart2,
  Plus, Edit2, Trash2, Play, Save, X, Share2,
} from 'lucide-react';
import { ReportChart, money } from './teamReportChart.jsx';
import { useTeamMetrics } from './teamMetricsCatalog.jsx';
import {
  runTeamReport, runTeamReportFull, listTeamReports, createTeamReport, updateTeamReport, deleteTeamReport,
} from '../api.js';
import { toast } from './Toast.jsx';
import { confirmDialog } from './ui.jsx';

// ─ Export helpers (self-contained, mirrors ReportsClientsView) ──────────
async function exportToExcel(rows, columns, filename) {
  const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
  const data = rows.map((r) => Object.fromEntries(columns.map((c) => [c.label, c.get(r)])));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Team');
  XLSX.writeFile(wb, filename);
}
function exportToCSV(rows, columns, filename) {
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const body = rows.map((r) => columns.map((c) => `"${String(c.get(r) ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// LOCAL YYYY-MM-DD (avoid UTC rollover).
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function dateWindow(preset, from, to) {
  const now = new Date();
  if (preset === 'thisMonth') return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(now) };
  if (preset === 'last30') { const f = new Date(now); f.setDate(f.getDate() - 30); return { from: ymd(f), to: ymd(now) }; }
  if (preset === 'thisYear') return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: ymd(now) };
  if (preset === 'custom') return { from: from || '', to: to || '' };
  return { from: '', to: '' }; // all time
}

// Policy type / status options — backend PolicyType / PolicyStatus enum values.
const POLICY_TYPE_OPTIONS = [
  { v: 'HEALTH', es: 'Salud (ACA)', en: 'Health (ACA)' },
  { v: 'DENTAL', es: 'Dental', en: 'Dental' },
  { v: 'VISION', es: 'Visión', en: 'Vision' },
  { v: 'LIFE', es: 'Vida', en: 'Life' },
  { v: 'MEDICARE_ADVANTAGE', es: 'Medicare Advantage', en: 'Medicare Advantage' },
  { v: 'MEDICARE_SUPPLEMENT', es: 'Medicare Suplementario', en: 'Medicare Supplement' },
  { v: 'AUTO', es: 'Auto', en: 'Auto' },
  { v: 'HOME', es: 'Hogar', en: 'Home' },
  { v: 'RENTERS', es: 'Inquilinos', en: 'Renters' },
  { v: 'FLOOD', es: 'Inundación', en: 'Flood' },
  { v: 'DISABILITY', es: 'Discapacidad', en: 'Disability' },
  { v: 'LONG_TERM_CARE', es: 'Cuidado largo plazo', en: 'Long-term care' },
  { v: 'ANNUITY', es: 'Anualidad', en: 'Annuity' },
  { v: 'UMBRELLA', es: 'Paraguas', en: 'Umbrella' },
  { v: 'COMMERCIAL', es: 'Comercial', en: 'Commercial' },
  { v: 'WORKERS_COMP', es: 'Workers Comp', en: 'Workers Comp' },
  { v: 'PET', es: 'Mascotas', en: 'Pet' },
  { v: 'TRAVEL', es: 'Viaje', en: 'Travel' },
];
const POLICY_STATUS_OPTIONS = [
  { v: 'ACTIVE', es: 'Activa', en: 'Active' },
  { v: 'PENDING', es: 'Pendiente', en: 'Pending' },
  { v: 'EXPIRED', es: 'Expirada', en: 'Expired' },
  { v: 'CANCELLED', es: 'Cancelada', en: 'Cancelled' },
  { v: 'LAPSED', es: 'Caducada', en: 'Lapsed' },
  { v: 'GRACE_PERIOD', es: 'Período de gracia', en: 'Grace period' },
];
// Los ayudantes de metas (GOAL_METRIC_LABELS, PERIOD_LABELS, fmtGoalValue)
// viven en teamMetricsCatalog.jsx, que es quien los usa.

const STAGE_LABELS = {
  NEW: { es: 'Nuevo', en: 'New' },
  CONTACTED: { es: 'Contactado', en: 'Contacted' },
  QUALIFIED: { es: 'Calificado', en: 'Qualified' },
  PROPOSAL: { es: 'Cotizado', en: 'Proposal' },
  WON: { es: 'Ganado', en: 'Won' },
};

// Metrics whose rows are NOT team members: filtering these by userId would
// empty the table, and the member picker is hidden for them in the builder.
const ROWS_ARE_NOT_PEOPLE = ['policies', 'goals', 'funnel'];

export default function ReportsTeamView({ lang = 'en', currentUser }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);

  const METRICS = useTeamMetrics(lang);
  const nameCol = { key: 'name', label: t('Miembro', 'Member'), get: (r) => r.name };
  const canManage = currentUser?.role !== 'assistant'; // seats can view, not build

  const [tab, setTab] = useState('builtin'); // builtin | saved
  const [results, setResults] = useState(null); // { title, metric, columns, rows, from, to, memberIds }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Run a metric with optional column/member/date config → opens results view.
  const run = async (cfg) => {
    const metric = cfg.metric;
    const meta = METRICS[metric];
    if (!meta) return;
    const win = cfg.from !== undefined ? { from: cfg.from, to: cfg.to } : { from: '', to: '' };
    // Chart defaults: bars over the first additive column. A saved report may
    // carry its own choice; fall back if that column no longer exists on the
    // metric (the builder can be edited to a different metric).
    const chartable = meta.columns.filter((c) => c.raw);
    const savedKey = chartable.some((c) => c.key === cfg.chartKey) ? cfg.chartKey : null;
    setResults({
      ...cfg,
      title: cfg.title || meta.title,
      metric,
      _meta: meta,
      chartType: cfg.chartType || 'bar',
      chartKey: savedKey || chartable.find((c) => c.additive)?.key || chartable[0]?.key || '',
    });
    setLoading(true);
    setError('');
    try {
      const params = { ...win };
      if (metric === 'policies') {
        params.policyTypes = cfg.policyTypes;
        params.policyStatuses = cfg.policyStatuses;
        params.groupBy = cfg.groupBy;
        params.dateField = cfg.dateField;
      }
      const payload = await runTeamReportFull(metric, params);
      let rows = payload.rows || [];
      // `funnel` also reports how many leads carry no stage history at all.
      const extras = { leadsNoHistory: payload.leadsNoHistory };
      // The member filter only applies to per-user metrics. `policies` groups by
      // who processed, `goals` rows are goals and `funnel` rows are stages —
      // all three carry synthetic ids, so filtering them by userId would empty
      // the table.
      if (!ROWS_ARE_NOT_PEOPLE.includes(metric) && Array.isArray(cfg.memberIds) && cfg.memberIds.length) {
        rows = rows.filter((r) => cfg.memberIds.includes(r.userId));
      }
      setResults((prev) => ({ ...prev, rows, ...extras }));
    } catch (err) {
      setError(err.message || String(err));
      setResults((prev) => ({ ...prev, rows: [] }));
    } finally {
      setLoading(false);
    }
  };

  // Resolve which columns a results view shows: name + selected (or all).
  const resolveColumns = (metric, selectedKeys, groupBy) => {
    const all = METRICS[metric]?.columns || [];
    const picked = (selectedKeys && selectedKeys.length) ? all.filter((c) => selectedKeys.includes(c.key)) : all;
    let firstCol = nameCol;
    if (metric === 'policies') {
      firstCol = {
        key: 'name',
        label: groupBy === 'agent' ? t('Agente', 'Agent')
          : groupBy === 'analyst' ? t('Analista', 'Analyst')
          : groupBy === 'enrollmentPeriod' ? t('Período (OE/SEP)', 'Period (OE/SEP)')
          : groupBy === 'generalAgent' ? t('Agente general', 'General agent')
          : t('Procesó', 'Processed by'),
        get: (r) => r.name,
      };
    } else if (metric === 'funnel') {
      firstCol = { key: 'name', label: t('Etapa', 'Stage'), get: (r) => STAGE_LABELS[r.name]?.[es ? 'es' : 'en'] || r.name };
    } else if (metric === 'goals') {
      // A goal with no agent belongs to the whole agency; the backend leaves
      // the wording to us so it stays translated.
      firstCol = { key: 'name', label: t('Miembro', 'Member'), get: (r) => (r.isAgencyWide ? t('Toda la agencia', 'Whole agency') : r.name) };
    }
    return [firstCol, ...picked];
  };

  // ── RESULTS VIEW ──────────────────────────────────────────────────────
  if (results) {
    const cols = resolveColumns(results.metric, results.columns, results.groupBy);
    const rows = results.rows || [];
    const Icon = results._meta?.icon || BarChart2;
    const stamp = ymd(new Date());
    const inp = { padding: '.45rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.82rem', outline: 'none', fontFamily: 'inherit' };
    return (
      <div style={{ padding: '1.5rem', maxWidth: 1000, margin: '0 auto' }}>
        <button onClick={() => { setResults(null); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', fontSize: '.82rem', marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> {t('Volver', 'Back')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.75rem', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon size={20} color={results._meta?.color || '#00d4ff'} /> {results.title}
          </h2>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => exportToExcel(rows, cols, `team-${results.metric}-${stamp}.xlsx`)} disabled={!rows.length} style={{ ...inp, cursor: rows.length ? 'pointer' : 'not-allowed', opacity: rows.length ? 1 : .5, display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700 }}><Download size={13} /> Excel</button>
            <button onClick={() => exportToCSV(rows, cols, `team-${results.metric}-${stamp}.csv`)} disabled={!rows.length} style={{ ...inp, cursor: rows.length ? 'pointer' : 'not-allowed', opacity: rows.length ? 1 : .5, display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700 }}><Download size={13} /> CSV</button>
          </div>
        </div>
        {error && <div style={{ background: '#3a1a1a', border: '1px solid #ef4444', borderRadius: 10, padding: '.7rem 1rem', marginBottom: 12, color: '#fca5a5', fontSize: '.85rem' }}>{error}</div>}

        <ReportChart
          rows={rows}
          meta={results._meta}
          chartType={results.chartType}
          chartKey={results.chartKey}
          onChartType={(v) => setResults((p) => ({ ...p, chartType: v }))}
          onChartKey={(v) => setResults((p) => ({ ...p, chartKey: v }))}
          loading={loading}
          t={t}
          inp={inp}
        />

        <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--c-text2,#7aa3c0)' }}>{t('Cargando…', 'Loading…')}</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--c-text2,#7aa3c0)', fontSize: '.88rem' }}>
              {results.metric === 'goals'
                ? t('No hay metas activas para el período que corre ahora. Créalas en Metas.', 'No active goals for the period running now. Create them under Goals.')
                : t('Sin datos para este rango.', 'No data for this range.')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
              <thead>
                <tr style={{ background: 'var(--c-surface2,#112538)' }}>
                  {cols.map((col, i) => (
                    <th key={col.key} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '.6rem .9rem', color: 'var(--c-text2,#7aa3c0)', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--c-border,#1a3550)' }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userId} style={{ borderBottom: '1px solid var(--c-border,#1a3550)' }}>
                    {cols.map((col, i) => (
                      <td key={col.key} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '.55rem .9rem', color: i === 0 ? 'var(--c-white,#eef6ff)' : 'var(--c-text,#c8dff0)', fontWeight: i === 0 ? 700 : 500 }}>{col.get(r)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ── CATALOG / SAVED (tabs) ────────────────────────────────────────────
  return (
    <div style={{ padding: '1.5rem', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '.35rem' }}>
        <BarChart2 size={22} color="#00d4ff" />
        <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--c-white,#eef6ff)' }}>{t('Reportes de Equipo', 'Team Reports')}</h2>
      </div>
      <div style={{ fontSize: '.85rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '1rem' }}>
        {t('Desempeño por miembro del equipo (agentes y seats). Filtra por fecha y exporta a Excel.', 'Performance per team member (agents and seats). Filter by date and export to Excel.')}
      </div>

      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1.25rem' }}>
        {[['builtin', t('Pre-armados', 'Built-in')], ['saved', t('Mis reportes', 'My reports')]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: '.4rem .9rem', borderRadius: 8, border: `1px solid ${tab === id ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)'}`, background: tab === id ? 'var(--c-cyan-dim,#0a2c3d)' : 'transparent', color: tab === id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text3,#45688a)', fontSize: '.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'builtin' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1rem' }}>
          {Object.entries(METRICS).map(([id, meta]) => (
            <button key={id} onClick={() => run({ metric: id, from: '', to: '' })}
              style={{ textAlign: 'left', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.1rem', cursor: 'pointer', fontFamily: 'inherit' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = meta.color + '88'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-border,#1a3550)'; }}>
              <div style={{ width: 42, height: 42, borderRadius: 11, background: meta.color + '1a', border: `1px solid ${meta.color}44`, display: 'grid', placeItems: 'center', marginBottom: '.7rem' }}>
                <meta.icon size={20} color={meta.color} />
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: 4 }}>{meta.title}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>{meta.desc}</div>
            </button>
          ))}
        </div>
      ) : (
        <SavedReports lang={lang} METRICS={METRICS} canManage={canManage} currentUser={currentUser}
          onRun={(report) => {
            const cfg = report.config || {};
            const win = dateWindow(cfg.datePreset || 'all', cfg.from, cfg.to);
            run({
              metric: cfg.metric, columns: cfg.columns, memberIds: cfg.memberIds, title: report.name,
              from: win.from, to: win.to,
              policyTypes: cfg.policyTypes, policyStatuses: cfg.policyStatuses, groupBy: cfg.groupBy, dateField: cfg.dateField,
              chartType: cfg.chartType, chartKey: cfg.chartKey,
            });
          }} />
      )}
    </div>
  );
}

// ── "Mis reportes" tab — list + builder ──────────────────────────────────
function SavedReports({ lang, METRICS, canManage, currentUser, onRun }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // report being edited, or {} for new
  const [members, setMembers] = useState([]); // [{userId, name}] for the member picker

  const refresh = async () => {
    setLoading(true);
    try { setReports(await listTeamReports()); }
    catch (err) { toast.error((es ? 'No se pudo cargar: ' : "Couldn't load: ") + err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  // Member list for the builder's picker — derived from a production run
  // (returns every member incl. seats).
  useEffect(() => {
    runTeamReport('production').then((rows) => setMembers(rows.map((r) => ({ userId: r.userId, name: r.name })))).catch(() => {});
  }, []);

  const metricLabel = (m) => METRICS[m]?.title || m;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        {canManage && (
          <button onClick={() => setEditing({})} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', padding: '.55rem 1rem', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: '.82rem', fontFamily: 'inherit' }}>
            <Plus size={15} /> {t('Nuevo reporte', 'New report')}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--c-text2,#7aa3c0)' }}>{t('Cargando…', 'Loading…')}</div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem', background: 'var(--c-surface,#0d1c2e)', border: '1px dashed var(--c-border,#1a3550)', borderRadius: 14, color: 'var(--c-text2,#7aa3c0)', fontSize: '.88rem' }}>
          {t('Aún no tienes reportes guardados. Crea uno con "Nuevo reporte".', 'No saved reports yet. Create one with "New report".')}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '1rem' }}>
          {reports.map((r) => {
            const meta = METRICS[r.config?.metric];
            const Icon = meta?.icon || BarChart2;
            return (
              <div key={r.id} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={18} color={meta?.color || '#00d4ff'} />
                  <div style={{ fontWeight: 700, color: 'var(--c-white,#eef6ff)', fontSize: '.95rem', flex: 1, minWidth: 0 }}>{r.name}</div>
                  {r.sharedWithAgency && <Share2 size={13} color="#7aa3c0" title={t('Compartido', 'Shared')} />}
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--c-text3,#45688a)' }}>
                  {metricLabel(r.config?.metric)}{!r.isOwner && r.owner ? ` · ${t('por', 'by')} ${r.owner.displayName}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                  <button onClick={() => onRun(r)} style={btn('primary')}><Play size={12} /> {t('Correr', 'Run')}</button>
                  {canManage && r.isOwner && <button onClick={() => setEditing(r)} style={btn('ghost')} title={t('Editar', 'Edit')}><Edit2 size={12} /></button>}
                  {canManage && r.isOwner && (
                    <button onClick={async () => { if (await confirmDialog({ message: t(`¿Eliminar "${r.name}"?`, `Delete "${r.name}"?`), danger: true })) { try { await deleteTeamReport(r.id); refresh(); } catch (e) { toast.error(e.message); } } }} style={btn('danger')} title={t('Eliminar', 'Delete')}><Trash2 size={12} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <BuilderModal lang={lang} METRICS={METRICS} members={members} initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function btn(kind) {
  const map = {
    primary: { bg: '#00d4ff22', color: '#00d4ff', border: '#00d4ff66' },
    ghost: { bg: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', border: 'var(--c-border,#1a3550)' },
    danger: { bg: '#7f1d1d22', color: '#fca5a5', border: '#ef444466' },
  }[kind];
  return { background: map.bg, color: map.color, border: `1px solid ${map.border}`, padding: '.4rem .65rem', borderRadius: 8, cursor: 'pointer', fontSize: '.75rem', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' };
}

// ── Builder modal — name, metric, columns, members, date preset, share ────
function BuilderModal({ lang, METRICS, members, initial, onClose, onSaved }) {
  const es = lang === 'es';
  const t = (a, b) => (es ? a : b);
  const cfg0 = initial.config || {};
  const [name, setName] = useState(initial.name || '');
  const [metric, setMetric] = useState(cfg0.metric || 'production');
  const [columns, setColumns] = useState(Array.isArray(cfg0.columns) ? cfg0.columns : []);
  const [memberIds, setMemberIds] = useState(Array.isArray(cfg0.memberIds) ? cfg0.memberIds : []);
  const [datePreset, setDatePreset] = useState(cfg0.datePreset || 'all');
  const [from, setFrom] = useState(cfg0.from || '');
  const [to, setTo] = useState(cfg0.to || '');
  const [shared, setShared] = useState(!!initial.sharedWithAgency);
  const [busy, setBusy] = useState(false);
  // Advanced "policies" filters.
  const [policyTypes, setPolicyTypes] = useState(Array.isArray(cfg0.policyTypes) ? cfg0.policyTypes : []);
  const [policyStatuses, setPolicyStatuses] = useState(Array.isArray(cfg0.policyStatuses) ? cfg0.policyStatuses : []);
  const [groupBy, setGroupBy] = useState(cfg0.groupBy || 'processedBy');
  const [dateField, setDateField] = useState(cfg0.dateField || 'created');
  // Saved view: which chart the report opens with.
  const [chartType, setChartType] = useState(cfg0.chartType || 'bar');
  const [chartKey, setChartKey] = useState(cfg0.chartKey || '');

  const isPolicies = metric === 'policies';
  // Goals rows are goals, not people, and they always read the period running
  // now — so neither the member picker nor the date range applies to them.
  const isGoals = metric === 'goals';
  const metricCols = METRICS[metric]?.columns || [];
  const chartableCols = metricCols.filter((c) => c.raw);
  // Reset column picks when the metric changes to columns that no longer exist.
  useEffect(() => { setColumns((prev) => prev.filter((k) => metricCols.some((c) => c.key === k))); /* eslint-disable-next-line */ }, [metric]);
  // Same for the charted measure — a stale key would silently fall back at run
  // time, so clear it here where the user can see the picker reset.
  useEffect(() => {
    setChartKey((prev) => (chartableCols.some((c) => c.key === prev) ? prev : (chartableCols.find((c) => c.additive)?.key || '')));
    /* eslint-disable-next-line */
  }, [metric]);
  // A pie of averages or percentages would imply the slices split a whole.
  const pieAllowed = chartableCols.find((c) => c.key === chartKey)?.additive === true;
  useEffect(() => { if (chartType === 'pie' && !pieAllowed) setChartType('bar'); /* eslint-disable-next-line */ }, [chartKey, pieAllowed]);
  const toggleCol = (k) => setColumns((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const toggleMember = (id) => setMemberIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleIn = (setter) => (v) => setter((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const save = async () => {
    if (!name.trim()) { toast.error(t('Ponle un nombre', 'Add a name')); return; }
    setBusy(true);
    const config = { metric, columns, memberIds: memberIds.length ? memberIds : null, datePreset, from: datePreset === 'custom' ? from : '', to: datePreset === 'custom' ? to : '', chartType, chartKey };
    if (isPolicies) {
      config.policyTypes = policyTypes;
      config.policyStatuses = policyStatuses;
      config.groupBy = groupBy;
      config.dateField = dateField;
    }
    const payload = { name: name.trim(), config, sharedWithAgency: shared };
    try {
      if (initial.id) await updateTeamReport(initial.id, payload);
      else await createTeamReport(payload);
      toast.success(t('Reporte guardado', 'Report saved'));
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const inp = { width: '100%', padding: '.55rem .7rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.88rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl = { fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5, display: 'block' };
  const chip = (on, color) => ({ padding: '.3rem .65rem', borderRadius: 7, border: `1px solid ${on ? color + '66' : 'var(--c-border,#1a3550)'}`, background: on ? color + '18' : 'transparent', color: on ? color : 'var(--c-text3,#45688a)', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 16, padding: '1.5rem', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--c-white,#eef6ff)' }}>{initial.id ? t('Editar reporte', 'Edit report') : t('Nuevo reporte de equipo', 'New team report')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Nombre', 'Name')}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('ej. Producción del mes', 'e.g. This month production')} style={inp} />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Tipo de reporte', 'Report type')}</label>
          <select value={metric} onChange={(e) => setMetric(e.target.value)} style={inp}>
            {Object.entries(METRICS).map(([id, m]) => <option key={id} value={id}>{m.title}</option>)}
          </select>
        </div>

        {isPolicies && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={lbl}>{t('Tipo de póliza (vacío = todas)', 'Policy type (empty = all)')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto' }}>
                {POLICY_TYPE_OPTIONS.map((o) => {
                  const on = policyTypes.includes(o.v);
                  return <button key={o.v} type="button" onClick={() => toggleIn(setPolicyTypes)(o.v)} style={chip(on, '#f59e0b')}>{es ? o.es : o.en}</button>;
                })}
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={lbl}>{t('Estado (vacío = todos)', 'Status (empty = all)')}</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {POLICY_STATUS_OPTIONS.map((o) => {
                  const on = policyStatuses.includes(o.v);
                  return <button key={o.v} type="button" onClick={() => toggleIn(setPolicyStatuses)(o.v)} style={chip(on, '#10b981')}>{es ? o.es : o.en}</button>;
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('Agrupar por', 'Group by')}</label>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} style={inp}>
                  <option value="processedBy">{t('Quién procesó', 'Who processed')}</option>
                  <option value="analyst">{t('Analista (quién vendió)', 'Analyst (who sold)')}</option>
                  <option value="agent">{t('Agente dueño', 'Owning agent')}</option>
                  {/* Lissette 2026-08-10: "¿cuántas pólizas por período OE/SEP?"
                      y "¿producción por agente general?". Ambos campos viven en
                      la póliza desde los PR #1558 y #1560. */}
                  <option value="enrollmentPeriod">{t('Período de inscripción (OE/SEP)', 'Enrollment period (OE/SEP)')}</option>
                  <option value="generalAgent">{t('Agente general (MGA)', 'General agent (MGA)')}</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>{t('Fecha por', 'Date by')}</label>
                <select value={dateField} onChange={(e) => setDateField(e.target.value)} style={inp}>
                  <option value="created">{t('Fecha de creación', 'Created date')}</option>
                  <option value="effective">{t('Fecha efectiva', 'Effective date')}</option>
                </select>
              </div>
            </div>
          </>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Columnas (vacío = todas)', 'Columns (empty = all)')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {metricCols.map((c) => {
              const on = columns.includes(c.key);
              return <button key={c.key} type="button" onClick={() => toggleCol(c.key)} style={{ padding: '.3rem .65rem', borderRadius: 7, border: `1px solid ${on ? '#00d4ff66' : 'var(--c-border,#1a3550)'}`, background: on ? '#00d4ff18' : 'transparent', color: on ? '#00d4ff' : 'var(--c-text3,#45688a)', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{c.label}</button>;
            })}
          </div>
        </div>

        {!ROWS_ARE_NOT_PEOPLE.includes(metric) && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>{t('Miembros (vacío = todos)', 'Members (empty = all)')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
              {members.length === 0 && <span style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{t('Cargando miembros…', 'Loading members…')}</span>}
              {members.map((m) => {
                const on = memberIds.includes(m.userId);
                return <button key={m.userId} type="button" onClick={() => toggleMember(m.userId)} style={chip(on, '#a78bfa')}>{m.name}</button>;
              })}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={lbl}>{t('Gráfico', 'Chart')}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={chartType} onChange={(e) => setChartType(e.target.value)} style={{ ...inp, flex: '0 0 auto', width: 'auto' }}>
              <option value="bar">{t('Barras', 'Bars')}</option>
              <option value="pie" disabled={!pieAllowed}>{t('Torta', 'Pie')}</option>
              <option value="none">{t('Solo tabla', 'Table only')}</option>
            </select>
            <select value={chartKey} onChange={(e) => setChartKey(e.target.value)} style={{ ...inp, flex: 1, minWidth: 140 }} disabled={chartType === 'none'}>
              {chartableCols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {!pieAllowed && chartKey && (
            <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 5, lineHeight: 1.45 }}>
              {t('La torta se desactiva en promedios y porcentajes: sus partes no reparten un total.', 'Pie is off for averages and percentages — their parts do not divide a whole.')}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1rem', display: isGoals ? 'none' : undefined }}>
          <label style={lbl}>{t('Rango de fecha', 'Date range')}</label>
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} style={inp}>
            <option value="all">{t('Todo el tiempo', 'All time')}</option>
            <option value="thisMonth">{t('Este mes', 'This month')}</option>
            <option value="last30">{t('Últimos 30 días', 'Last 30 days')}</option>
            <option value="thisYear">{t('Este año', 'This year')}</option>
            <option value="custom">{t('Personalizado', 'Custom')}</option>
          </select>
          {datePreset === 'custom' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <DateField value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
              <DateField value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
            </div>
          )}
        </div>

        {isGoals && (
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '1rem', lineHeight: 1.5 }}>
            {t('Este reporte siempre mide el período que corre ahora (el mes, trimestre o año de cada meta), así que no lleva rango de fecha.',
               'This report always measures the period running now (each goal’s month, quarter or year), so it takes no date range.')}
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: '1.25rem' }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} style={{ width: 17, height: 17, cursor: 'pointer' }} />
          <span style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)' }}>{t('Compartir con mi agencia (solo lectura)', 'Share with my agency (read-only)')}</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
          <button onClick={onClose} style={{ padding: '.6rem 1.1rem', borderRadius: 9, background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border,#1a3550)', cursor: 'pointer', fontWeight: 700, fontSize: '.82rem' }}>{t('Cancelar', 'Cancel')}</button>
          <button onClick={save} disabled={busy} style={{ padding: '.6rem 1.25rem', borderRadius: 9, background: 'linear-gradient(135deg,#00d4ff,#00a8c8)', color: '#0a1628', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '.82rem', opacity: busy ? .6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Save size={14} /> {busy ? t('Guardando…', 'Saving…') : t('Guardar', 'Save')}</button>
        </div>
      </div>
    </div>
  );
}
